import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { RouterModule } from '@angular/router';
import { Subject, of, forkJoin } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { BreadcrumbService } from '@core/services/breadcrumb.service';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateAdapter } from '@angular/material/core';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { DateRangePickerComponent } from '@shared/components/date-range-picker/date-range-picker.component';
import { UnitSelectorComponent } from '@shared/components/unit-selector/unit-selector.component';
import { DivineServiceApiService } from '../../services/api/divine-service-api.service';
import { DivineServiceDetailDto, DivineServiceTypeDto, getDivineServiceTypeName } from '../../models/divine-service.model';
import { FilterChip, TableColumn, PaginationConfig } from '@shared/components/data-table/data-table.component';
import { ToolbarSortSelection } from '@shared/components/table-toolbar/table-toolbar.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { NotificationService } from '@shared/services/notification.service';
import { OrganizationUnitItem } from '../../../user/models/filter-data.model';
import { OrganizationUnitApiService, OrganizationUnitTreeNodeDto } from '@core/api/services/organization-unit-api.service';
import { PersonService } from '@features/people/services/person.service';
import { UserFullInfoService } from '@core/services/user-full-info.service';

/**
 * Componente de listado de servicios divinos
 * Muestra una tabla con filtros por fecha y unidad, y acciones para gestionar servicios
 */
@Component({
  selector: 'app-divine-services-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    DateRangePickerComponent,
    UnitSelectorComponent,
    TableSurfaceComponent,
  ],
  templateUrl: './divine-services-list.component.html',
  styleUrl: './divine-services-list.component.scss'
})
export class DivineServicesListComponent implements OnInit, OnDestroy {
  private readonly allColumns: TableColumn[] = [
    { key: 'date', header: 'Fecha', sortable: true, hideable: false, visible: false },
    { key: 'type', header: 'Tipo SD', sortable: true, hideable: false, visible: false },
    { key: 'serviceInfo', header: 'Servicio Divino', sortable: false, hideable: true, visible: true },
    { key: 'community', header: 'Comunidad', sortable: true, hideable: true, visible: true },
    { key: 'bibleText', header: 'Texto Biblico', sortable: false, hideable: true, visible: true },
    { key: 'attendance', header: 'Concurrencia', sortable: true, hideable: true, visible: true },
    { key: 'status', header: 'Estado', sortable: false, hideable: true, visible: true, width: '110px' }
  ];
  tableData: DivineServiceDetailDto[] = [];
  isLoading = false;
  activeFilterChips: FilterChip[] = [];
  canManageDivineServices = false;

  // Filtros como FormControls
  selectedUnitIdControl = new FormControl('');
  searchControl = new FormControl('');
  selectedTypeIdControl = new FormControl<number | null>(null);
  dateRangeGroup = new FormGroup({
    start: new FormControl<any | null>(null),
    end: new FormControl<any | null>(null)
  });

  // Ordenamiento
  sortBy: string = 'date'; // Por defecto ordena por fecha
  sortDirection: 'asc' | 'desc' = 'desc'; // Por defecto descendente (reciente primero)

  // Paginación
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 0;
  paginationConfig: PaginationConfig = {
    currentPage: this.currentPage,
    pageSize: this.pageSize,
    totalCount: this.totalCount,
    totalPages: this.totalPages
  };

  // Exponer utilidades para el template
  Math = Math;
  getDivineServiceTypeName = getDivineServiceTypeName;

  // Datos de filtros
  // units ya no es necesario - UnitSelectorComponent busca bajo demanda

  private destroy$ = new Subject<void>();
  protected unitId: string | null = null;
  unitName: string | null = null;
  showUnitFilter = false;
  unitFilterSource: OrganizationUnitItem[] = [];
  serviceTypes: DivineServiceTypeDto[] = [];
  filtersExpanded = false;

  readonly COMMUNITY_LEVEL = 5;

  get backRoute(): string {
    return this.unitId ? `/units/${this.unitId}` : '/';
  }

  get currentSort(): ToolbarSortSelection {
    return { sortBy: this.getFrontendSortKey(this.sortBy), direction: this.sortDirection };
  }

  get columns(): TableColumn[] {
    return [...this.allColumns];
  }

  get showCommunityInMobileCards(): boolean {
    return this.allColumns.find(column => column.key === 'community')?.visible !== false;
  }

  constructor(
    private divineServiceService: DivineServiceApiService,
    private organizationUnitService: OrganizationUnitApiService,
    private personService: PersonService,
    private userFullInfoService: UserFullInfoService,
    private router: Router,
    private route: ActivatedRoute,
    private dateAdapter: DateAdapter<any>,
    private cdr: ChangeDetectorRef,
    private notificationService: NotificationService,
    private breadcrumbService: BreadcrumbService
  ) { }

  ngOnInit(): void {
    const allParams = this.route.snapshot.pathFromRoot.reduce(
      (acc, r) => ({ ...acc, ...r.params }), {} as Record<string, string>
    );
    this.unitId = allParams['unit-id'] ?? null;

    this.initializeDefaultDateFilters();
    this.loadDivineServices();
    this.loadServiceTypes();
    this.setupFilters();
    this.resolveUnitInfo();
    this.userFullInfoService.getUserInfo$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.canManageDivineServices = this.userFullInfoService.canManageDivineServicesSnapshot(user);
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Inicializa los filtros de fecha con los valores por defecto:
   * - Desde: Primer día del mes anterior
   * - Hasta: Último día del mes anterior
   */
  private resolveUnitInfo(): void {
    if (!this.unitId) return;

    this.organizationUnitService.getById(this.unitId)
      .pipe(
        takeUntil(this.destroy$),
        tap(unit => {
          this.unitName = unit.name ?? null;
          if (unit.name) {
            this.breadcrumbService.setDynamicCrumb('unit-id', unit.name);
          }
          this.setCommunityColumnVisible(unit.organizationLevelId !== this.COMMUNITY_LEVEL);
        }),
        switchMap(unit => {
          if (unit.organizationLevelId === this.COMMUNITY_LEVEL) {
            this.showUnitFilter = false;
            return of(null);
          }
          return forkJoin({
            tree: this.organizationUnitService.getDescendantTree(this.unitId!),
            authorizedUnits: this.personService.getAuthorizedUnits(),
          });
        })
      )
      .subscribe(result => {
        if (result) {
          const descendantIds = new Set(this.flattenTree((result.tree as any).children ?? []).map(unit => unit.id));
          const authorizedDescendants = result.authorizedUnits
            .filter(unit => descendantIds.has(unit.id));
          this.unitFilterSource = authorizedDescendants;
          this.showUnitFilter = authorizedDescendants.length > 0;
        }
        this.cdr.markForCheck();
      });
  }

  private flattenTree(nodes: OrganizationUnitTreeNodeDto[]): OrganizationUnitItem[] {
    return nodes.flatMap(node => [
      {
        id: node.id ?? '',
        name: node.name ?? '',
        levelId: node.organizationLevelId ?? 0,
        levelName: node.levelName ?? '',
        parentId: node.parentUnitId ?? undefined,
        ancestorNames: []
      },
      ...this.flattenTree(node.children ?? [])
    ]);
  }

  private setCommunityColumnVisible(visible: boolean): void {
    for (const column of this.allColumns) {
      if (column.key === 'community') {
        column.visible = visible;
      }
    }
  }

  private initializeDefaultDateFilters(): void {
    const today = new Date();

    const firstDayOfMonth = this.dateAdapter.createDate(today.getFullYear(), today.getMonth(), 1);
    const firstDayOfNextMonth = this.dateAdapter.addCalendarMonths(firstDayOfMonth, 1);
    const lastDayOfMonth = this.dateAdapter.addCalendarDays(firstDayOfNextMonth, -1);

    this.dateRangeGroup.setValue(
      { start: firstDayOfMonth, end: lastDayOfMonth },
      { emitEvent: false }
    );
  }

  /**
   * Configura los filtros con debounce para búsqueda automática
   */
  private setupFilters(): void {
    const reload = () => {
      this.currentPage = 1;
      this.loadDivineServices();
    };

    this.selectedUnitIdControl.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(reload);

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(reload);

    this.selectedTypeIdControl.valueChanges
      .pipe(debounceTime(100), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(reload);

    this.dateRangeGroup.valueChanges
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(reload);
  }

  /**
   * Carga los servicios divinos con los filtros actuales
   */
  private loadDivineServices(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    const unitId = this.selectedUnitIdControl.value || this.unitId || undefined;
    const startDate = this.dateRangeGroup.value.start
      ? this.toISODateString(this.dateRangeGroup.value.start)
      : undefined;
    const endDate = this.dateRangeGroup.value.end
      ? this.toISODateString(this.dateRangeGroup.value.end)
      : undefined;
    const searchTerm = this.searchControl.value?.trim() || undefined;
    const divineServiceTypeId = this.selectedTypeIdControl.value ?? undefined;

    this.divineServiceService.getDivineServices(
      this.currentPage,
      this.pageSize,
      unitId || undefined,
      startDate || undefined,
      endDate || undefined,
      this.sortBy,
      this.sortDirection,
      searchTerm,
      divineServiceTypeId
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.tableData = response.items;
          this.currentPage = response.pageNumber;
          this.totalCount = response.totalCount;
          this.totalPages = Math.ceil(response.totalCount / response.pageSize);
          this.paginationConfig = {
            currentPage: this.currentPage,
            pageSize: this.pageSize,
            totalCount: this.totalCount,
            totalPages: this.totalPages
          };
          this.buildFilterChips();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading divine services:', error);
          this.notificationService.error('Error al cargar los servicios divinos');
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Limpia los filtros y recarga la lista
   */
  onToolbarSearch(term: string): void {
    this.searchControl.setValue(term);
  }

  onToolbarSort(event: ToolbarSortSelection): void {
    this.onSortChange(event);
  }

  onFilterRemove(key: string): void {
    if (key === 'unit') {
      this.selectedUnitIdControl.reset(null, { emitEvent: false });
    } else if (key === 'dateRange') {
      this.initializeDefaultDateFilters();
    } else if (key === 'type') {
      this.selectedTypeIdControl.reset(null, { emitEvent: false });
    }

    this.currentPage = 1;
    this.buildFilterChips();
    this.loadDivineServices();
  }

  private toISODateString(date: any): string {
    const year = this.dateAdapter.getYear(date);
    const month = String(this.dateAdapter.getMonth(date) + 1).padStart(2, '0');
    const day = String(this.dateAdapter.getDate(date)).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Maneja el cambio de página
   */
  onPageChange(event: { page: number }): void {
    this.currentPage = event.page;
    this.loadDivineServices();
  }

  /**
   * Maneja el cambio de tamaño de página
   */
  onPageSizeChange(event: { pageSize: number }): void {
    this.pageSize = event.pageSize;
    this.currentPage = 1;
    this.loadDivineServices();
  }

  /**
   * Maneja el cambio de ordenamiento
   */
  onSortChange(event: { sortBy: string; direction: 'asc' | 'desc' }): void {
    // Mapear nombres de columnas del frontend a nombres del backend
    const columnMapping: { [key: string]: string } = {
      'serviceInfo': 'serviceInfo',
      'bibleText': 'bibleText',
      'date': 'startAt',
      'type': 'typeName',
      'community': 'organizationUnitName',
      'totalAdults': 'totalAdults',
      'totalMinors': 'totalMinors',
      'attendance': 'attendance'
    };

    this.sortBy = columnMapping[event.sortBy] || event.sortBy;
    this.sortDirection = event.direction;
    this.currentPage = 1; // Reset a primera página cuando cambia el ordenamiento
    this.loadDivineServices();
  }

  private loadServiceTypes(): void {
    this.divineServiceService.getDivineServiceTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe(types => {
        this.serviceTypes = [...types].sort((a, b) =>
          (a.name || a.displayName).localeCompare(b.name || b.displayName, 'es', { sensitivity: 'base' })
        );
        this.cdr.markForCheck();
      });
  }

  /**
   * Navega a crear nuevo servicio divino
   */
  createDivineService(): void {
    if (!this.canManageDivineServices) {
      return;
    }

    if (this.unitId) {
      this.router.navigate(['/units', this.unitId, 'divine-services', 'new']);
    } else {
      this.router.navigate(['/divine-services/new']);
    }
  }

  /**
   * Navega a ver detalles del servicio
   */
  viewDivineService(service: DivineServiceDetailDto): void {
    if (this.unitId) {
      this.router.navigate(['/units', this.unitId, 'divine-services', service.id]);
    } else {
      this.router.navigate(['/divine-services', service.id]);
    }
  }

  /**
   * Formatea una fecha para mostrar (solo la fecha sin hora)
   */
  formatDate(dateTime: string): string {
    const date = new Date(dateTime);
    const weekday = date.toLocaleDateString('es-AR', { weekday: 'long' });
    return `${weekday} ${this.formatNumericDate(date)}`;
  }

  formatMobileDate(dateTime: string): string {
    const date = new Date(dateTime);
    const weekday = date.toLocaleDateString('es-AR', { weekday: 'short' }).replace('.', '');
    return `${weekday} ${this.formatNumericDate(date)}`;
  }

  private formatNumericDate(date: Date): string {
    return date.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  isClosed(service: DivineServiceDetailDto): boolean {
    return service.isClosed === true || !!service.closedAtUtc;
  }

  private buildFilterChips(): void {
    const chips: FilterChip[] = [];
    const selectedUnitId = this.selectedUnitIdControl.value;
    const selectedUnit = selectedUnitId
      ? this.unitFilterSource.find(unit => unit.id === selectedUnitId)
      : null;

    if (selectedUnit) {
      chips.push({ key: 'unit', label: `Comunidad: ${selectedUnit.name}`, removable: true });
    }

    const selectedTypeId = this.selectedTypeIdControl.value;
    const selectedType = selectedTypeId != null
      ? this.serviceTypes.find(type => type.value === selectedTypeId)
      : null;

    if (selectedType) {
      chips.push({ key: 'type', label: `Tipo: ${selectedType.name || selectedType.displayName}`, removable: true });
    }

    const start = this.dateRangeGroup.value.start;
    const end = this.dateRangeGroup.value.end;
    if (start || end) {
      const startLabel = start ? this.toISODateString(start) : '...';
      const endLabel = end ? this.toISODateString(end) : '...';
      chips.push({ key: 'dateRange', label: `${startLabel} - ${endLabel}`, removable: true });
    }

    this.activeFilterChips = chips;
  }

  private getFrontendSortKey(sortBy: string): string {
    const mapping: Record<string, string> = {
      startAt: 'date',
      date: 'date',
      serviceInfo: 'date',
      typeName: 'type',
      type: 'type',
      organizationUnitName: 'community',
      community: 'community',
      attendeeCount: 'attendance',
      attendance: 'attendance',
    };

    return mapping[sortBy] ?? sortBy;
  }
}
